import React from 'react';
import _ from 'lodash';

import {
  LoadingOverlay,
  Table
} from 'iron-forge-react-modules';
import {
  NetSuiteRecordChip,
  currencyUtil,
  routeDirectory,
  tableUtil
} from 'modules';

import { DateTime } from 'luxon';
import { Link } from '@mui/material';
import * as sharedTypes from '../types';
import * as types from './types';
import * as dataService from './dataService';



const TableComponent = Table.genTableComponent<types.IColumnId>();
export type IColumnId = types.IColumnId;



export interface IProps {
  filter: sharedTypes.IFilter;
  columnsToRender: types.IColumnId[];
}

export const FC: React.FC<IProps> = props => {
  const {
    fnQueryPage,
    fnQuery,
    pageInfo,
    queryTuple
  } = dataService.useQuery(props.filter);

  tableUtil.useQueryOnFilterChange({
    filter: props.filter,
    fnQuery
  });

  const rows = React.useMemo(
    () => _.map(queryTuple.data.rows, (row): types.IRow => ({
      id: row.id,
      columnId2Value: {
        orderNumber: {
          type: 'component',
          sortableValue: row.orderNumber,
          label: (
            <Link
              href={routeDirectory.partner.storeDetail.path(row.storeId, row.id)}
              target="_blank">
              {row.orderNumber}
            </Link>
          )
        },
        orderDate: {
          type: 'string',
          label: row.orderDate.toFormat('yyyy-MM-dd')
        },
        orderAge: {
          type: 'string',
          label: DateTime.now().diff(row.orderDate).toFormat(`dd'd' hh'h' mm'm'`)
        },
        customerEmail: {
          type: 'string',
          sortableValue: 0,
          label: row.customerEmail
        },
        e1Record: {
          type: 'component',
          sortableValue: 0,
          label: row.e1RecordUrl ? <Link href={row.e1RecordUrl} target="_blank">E1</Link> : <div>-</div>
        },
        netSuiteCustomerRecord: {
          type: 'component',
          sortableValue: 0,
          label: <NetSuiteRecordChip.FC writeStatus={row.netSuiteCustomerRecord} />
        },
        netSuiteSalesOrderRecord: {
          type: 'component',
          sortableValue: 0,
          label: <NetSuiteRecordChip.FC writeStatus={row.netSuiteSalesOrderRecord} />
        },
        orderTotal: {
          type: 'string',
          sortableValue: row.totalCents.toNumber(),
          label: currencyUtil.convertBigCentsToDollars(row.totalCents)
        },
        shipment: {
          type: 'component',
          sortableValue: '',
          label: row.trackingUrl ? (
            <Link
              href={row.trackingUrl}
              target="_blank">
              {row.trackingNumber}
            </Link>
          ) : (
            <div>
              -
            </div>
          )
        },
        quantity: {
          type: 'string',
          label: row.totalQuantity.toString()
        },
        itemFulfillmentNetSuiteRecord: {
          type: 'component',
          sortableValue: row.netSuiteWriteStatus.type === 'success' ? row.netSuiteWriteStatus.transactionId : '',
          label: <NetSuiteRecordChip.FC writeStatus={row.netSuiteWriteStatus} />
        },
        paymentMethod: {
          type: 'string',
          label: row.paymentMethod
        },
        store: {
          type: 'string',
          label: row.storeName
        }
      }
    })),
    [queryTuple]
  );

  const columns = tableUtil.useSortedColumns({
    columnIdsToRender: props.columnsToRender,
    columns: [
      {
        id: 'orderNumber',
        label: "Order #"
      },
      {
        id: 'orderDate',
        label: "Order Date"
      },
      {
        id: 'orderAge',
        label: "Order Age"
      },
      {
        id: 'orderTotal',
        label: "Order Total"
      },
      {
        id: 'customerEmail',
        label: "Customer Email"
      },
      {
        id: 'netSuiteCustomerRecord',
        label: "NS Customer Record"
      },
      {
        id: 'netSuiteSalesOrderRecord',
        label: "NS Sales Order"
      },
      {
        id: 'e1Record',
        label: "E1 Record"
      },
      {
        id: 'shipment',
        label: "Shipment"
      },
      {
        id: 'quantity',
        label: 'Qty. Purchased'
      },
      {
        id: 'itemFulfillmentNetSuiteRecord',
        label: 'NS Fulfillment'
      },
      {
        id: 'paymentMethod',
        label: "Payment Method"
      },
      {
        id: 'store',
        label: "Store"
      }
    ]
  });

  return (
    <>
      <TableComponent
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
        rows={rows}
        initSortOptions={{
          columnId: 'orderDate',
          direction: 'descending'
        }}
        columns={columns} />

      <LoadingOverlay.FC active={queryTuple.loading} />
    </>
  );
};
